import { motion } from "framer-motion";
const EASE = [0.23, 1, 0.32, 1] as const;
const T = {
  x: 130,
  y: 135,
  r: 76
};
const NK = {
  x: 336,
  y: 135,
  r: 44
};
const RECEPTOR_ANGLES = [-150, -115, -80, -45, -15, 15, 45, 80, 115, 150, 180];
const ANTIBODY_ANGLES = [-45, -15, 15, 45];

/**
 * step 0: CCR4 expression · step 1: antibody binding · step 2: NK-cell mediated ADCC.
 */
export function MoaDiagram({
  step
}: {
  step: number;
}) {
  return <svg viewBox="0 0 400 270" className="block h-auto w-full" role="img" aria-label="Mechanism of action of mogamulizumab" data-id="element-343" data-mp="3bo39hknq7m2y">
      {/* Malignant T cell */}
      <motion.circle cx={T.x} cy={T.y} r={T.r} fill="#eef1f6" stroke="#8c99aa" strokeWidth={1.2} animate={{
      strokeDasharray: step === 2 ? "5 4" : "0 0"
    }} transition={{
      duration: 0.6
    }} data-id="element-344" data-mp="3lkzc59qy1dzl" />
      <path d={nucleus(T.x - 4, T.y + 2, 38)} fill="#1e3a5f" opacity={0.85} data-id="element-345" data-mp="10u8m8s9855p6" />
      <path d={`M${T.x - 22} ${T.y - 6} q 12 10 22 -2 q 8 -10 20 4`} fill="none" stroke="#eef1f6" strokeWidth={2} strokeLinecap="round" opacity={0.8} data-id="element-346" data-mp="15ss8ktrqcji2" />

      {/* CCR4 receptors */}
      {RECEPTOR_ANGLES.map((a, i) => <g key={a} transform={`rotate(${a} ${T.x} ${T.y})`} data-id="element-347" data-mp="27k1awxuwej4e" data-mpi={i}>
          <motion.g initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.4,
        delay: 0.3 + i * 0.04
      }} data-id="element-348" data-mp="27mngiry8aojz">
            <line x1={T.x + T.r} y1={T.y} x2={T.x + T.r + 9} y2={T.y} stroke="#2a6f73" strokeWidth={step === 0 ? 2.4 : 1.8} data-id="element-349" data-mp="1xny0l4pw7e6d" />
            <path d={`M${T.x + T.r + 13} ${T.y - 5} L${T.x + T.r + 9} ${T.y - 5} L${T.x + T.r + 9} ${T.y + 5} L${T.x + T.r + 13} ${T.y + 5}`} fill="none" stroke="#2a6f73" strokeWidth={step === 0 ? 2.4 : 1.8} strokeLinejoin="round" data-id="element-350" data-mp="1stk3mxvl0yzl" />
          </motion.g>
        </g>)}

      {/* Mogamulizumab bound to CCR4 */}
      {ANTIBODY_ANGLES.map((a, i) => <g key={a} transform={`rotate(${a} ${T.x} ${T.y})`} data-id="element-351" data-mp="108n7kp87hmik" data-mpi={i}>
          <motion.g initial={false} animate={{
        opacity: step >= 1 ? 1 : 0,
        x: step >= 1 ? 0 : 18
      }} transition={{
        duration: 0.6,
        delay: step >= 1 ? i * 0.08 : 0,
        ease: EASE
      }} data-id="element-352" data-mp="10bj8mmfcorb8">
            <path d={`M${T.x + T.r + 14} ${T.y - 7} L${T.x + T.r + 26} ${T.y} L${T.x + T.r + 14} ${T.y + 7} M${T.x + T.r + 26} ${T.y} L${T.x + T.r + 44} ${T.y}`} fill="none" stroke="#1e3a5f" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round" data-id="element-353" data-mp="v9ttovp0bthv" />
            {/* defucosylated Fc tip */}
            <circle cx={T.x + T.r + 44} cy={T.y} r={2.4} fill="#ffffff" stroke="#1e3a5f" strokeWidth={1.4} data-id="element-354" data-mp="1e8obkdjrpa29" />
          </motion.g>
        </g>)}

      {/* NK cell */}
      <motion.g initial={false} animate={{
      opacity: step === 2 ? 1 : 0,
      x: step === 2 ? 0 : 30
    }} transition={{
      duration: 0.7,
      ease: EASE
    }} data-id="element-355" data-mp="21wfmbcpntf1f">
        <circle cx={NK.x} cy={NK.y} r={NK.r} fill="#f1f6f6" stroke="#2a6f73" strokeWidth={1.2} data-id="element-356" data-mp="3recro48ynvo2" />
        <ellipse cx={NK.x + 8} cy={NK.y} rx={20} ry={17} fill="#2a6f73" opacity={0.35} data-id="element-357" data-mp="3mfru6cl3fq3b" />
        {[[-22, -16], [-26, 6], [-16, 22], [6, -28], [12, 26]].map(([dx, dy], i) => <circle key={i} cx={NK.x + dx} cy={NK.y + dy} r={2.6} fill="#2a6f73" data-id="element-358" data-mp="3ci4tedd1v0" data-mpi={i} />)}
        {/* FcγRIIIa engagement */}
        {[-15, 15].map(a => {
        const rad = a * Math.PI / 180;
        const fx = T.x + Math.cos(rad) * (T.r + 46);
        const fy = T.y + Math.sin(rad) * (T.r + 46);
        const ang = Math.atan2(fy - NK.y, fx - NK.x);
        const nx = NK.x + Math.cos(ang) * NK.r;
        const ny = NK.y + Math.sin(ang) * NK.r;
        return <line key={a} x1={fx} y1={fy} x2={nx} y2={ny} stroke="#2a6f73" strokeWidth={2} strokeLinecap="round" data-id="element-359" data-mp="wpft8maxjizt" />;
      })}
      </motion.g>

      {/* Cytotoxic granules released towards the target */}
      {step === 2 && [0, 1, 2].map(i => <motion.circle key={i} r={2.2} fill="#2a6f73" initial={{
      cx: NK.x - NK.r + 4,
      cy: NK.y - 10 + i * 10,
      opacity: 0
    }} animate={{
      cx: T.x + T.r + 30,
      cy: NK.y - 12 + i * 12,
      opacity: [0, 1, 0.25]
    }} transition={{
      duration: 1.2,
      delay: 0.6 + i * 0.15,
      ease: EASE
    }} data-id="element-360" data-mp="1s0npj4oyoho6" />)}

      {/* Labels */}
      <g fontFamily="Inter, sans-serif" fontSize={11} fill="#353a42" data-id="element-361" data-mp="2b01knpyffyg9">
        <text x={T.x} y={262} textAnchor="middle" data-id="element-362" data-mp="2axx9qo241z6h">
          Malignant T cell
        </text>
        <motion.text x={70} y={36} textAnchor="middle" animate={{
        opacity: step === 0 ? 1 : 0.55
      }} fill="#2a6f73" data-id="element-363" data-mp="2ft34mcdhqo6p">
          CCR4
        </motion.text>
        <motion.text x={258} y={20} animate={{
        opacity: step >= 1 ? 1 : 0
      }} fill="#1e3a5f" data-id="element-364" data-mp="2pnd0w55k06mp">
          Mogamulizumab (IgG1)
        </motion.text>
        <motion.text x={NK.x} y={NK.y + NK.r + 20} textAnchor="middle" animate={{
        opacity: step === 2 ? 1 : 0
      }} data-id="element-365" data-mp="j1yqk18xjbfa">
          NK cell · FcγRIIIa
        </motion.text>
        <motion.text x={300} y={80} textAnchor="middle" animate={{
        opacity: step === 2 ? 1 : 0
      }} fill="#1e3a5f" data-id="element-366" data-mp="92horuwsthol">
          ADCC
        </motion.text>
      </g>
    </svg>;
}
function nucleus(cx: number, cy: number, r: number) {
  const pts: string[] = [];
  for (let k = 0; k <= 64; k++) {
    const t = k / 64 * Math.PI * 2;
    const rr = r * (1 + 0.1 * Math.sin(5 * t + 0.6) + 0.05 * Math.sin(11 * t));
    pts.push(`${(cx + Math.cos(t) * rr).toFixed(2)} ${(cy + Math.sin(t) * rr).toFixed(2)}`);
  }
  return `M${pts.join(" L")} Z`;
}